import {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Controller, useFormContext } from "react-hook-form";
import {
  TextField,
  CircularProgress,
  Autocomplete,
  InputAdornment,
} from "@mui/material";
import { debounce, isEmpty } from "lodash";
import { ResponseTypeList } from "src/Pages/Company/Absences/api";
import __ from "src/utils/Translations";
import EmployeesAutocompleteOption from "src/Pages/Company/Absences/AbsenceRequest/EmployeesAutocomplete/EmployeesAutocompleteOption";
import {
  IEmployeeDetails,
  IEmployeesAutocomplete,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { getInputStyles } from "./EmployeesAutocomplete.styles";
import EmployeesAutocompleteAdornment from "./EmployeesAutocompleteAdornment";

const EmployeesAutocomplete: FC<IEmployeesAutocomplete> = ({
  name,
  label,
  enabled,
  queryKey,
  absenceTypeId,
  queryFn,
  additionalQueryParams = {},
  required = false,
  hasDefaultValue = false,
  setFirstAsDefault = false,
  disabled = false,
}) => {
  const { control, setError, clearErrors, setValue, watch, trigger } =
    useFormContext();
  const hasSetInitialValue = useRef(false);
  const previousQueryParams = useRef(additionalQueryParams);

  const [search, setSearch] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isRemovedAdornment, setIsRemovedAdornment] = useState(
    !hasDefaultValue
  );

  const chosenReplacer = watch("replacer");

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isSuccess,
  } = useInfiniteQuery<ResponseTypeList<IEmployeeDetails>>({
    queryKey: [queryKey, search, additionalQueryParams],
    queryFn: ({ pageParam }) =>
      queryFn({
        pageParam,
        name: search || "",
        ...additionalQueryParams,
      }),
    getNextPageParam: (lastPage: ResponseTypeList<IEmployeeDetails>) => {
      if (lastPage.page < lastPage.totalPages) {
        return lastPage.page + 1;
      }

      // Should return undefined in case there is no next page available (based on tanstack docs)
      // eslint-disable-next-line no-undefined
      return undefined;
    },
    enabled,
    initialPageParam: null,
  });

  useEffect(() => {
    if (
      setFirstAsDefault &&
      !hasSetInitialValue.current &&
      !isEmpty(data?.pages[0].content)
    ) {
      setValue(name, data?.pages[0].content[0]);
      setIsTyping(false);
      setIsRemovedAdornment(false);
      hasSetInitialValue.current = true;
    }
  }, [data, name, setFirstAsDefault, setValue]);

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      hasSetInitialValue.current = false;
    };
  }, [absenceTypeId]);

  // Special corner case when API doesn't return any approver
  // - i.e. current employee supervisor is removed by mistake
  useEffect(() => {
    if (!data || name !== "approver" || search.length > 0 || disabled) return;

    const noApproverConfigured = data?.pages[0]?.totalElements === 0;

    if (noApproverConfigured) {
      setError(name, {
        type: "custom",
        message: __("Brak wyznaczonego akceptanta - nie można złożyć wniosku."),
      });
    } else {
      clearErrors(name);
    }
  }, [data, name, setError, clearErrors, search.length, disabled]);

  // Another special corner case - replacer is already selected and not present at chosen date range
  // when user has changed date range, replacer value should be set manually to refreshed employee data
  useEffect(() => {
    if (isSuccess && name === "replacer") {
      const hasQueryParamsChanged =
        JSON.stringify(previousQueryParams.current) !==
        JSON.stringify(additionalQueryParams);

      if (hasQueryParamsChanged) {
        previousQueryParams.current = additionalQueryParams;
        if (
          chosenReplacer?.employeeId === data?.pages[0].content[0]?.employeeId
        ) {
          setValue(name, data?.pages[0].content[0]);

          trigger("replacer");
        }
      }
    }
  }, [
    additionalQueryParams,
    chosenReplacer,
    data?.pages,
    name,
    isSuccess,
    setValue,
    trigger,
  ]);

  const options = useMemo(
    () => data?.pages.flatMap((page) => page.content) || [],
    [data?.pages]
  );

  const debouncedSearch = useRef(
    debounce((__: SyntheticEvent, value: string) => {
      setSearch(value);
    }, 300)
  ).current;

  const handleSearchChange = useCallback(debouncedSearch, [debouncedSearch]);

  useEffect(
    () => () => {
      debouncedSearch.cancel();
    },
    [debouncedSearch]
  );

  const handleScroll = (event: SyntheticEvent) => {
    const listbox = event.currentTarget;
    const scrollPercentage =
      (listbox.scrollTop + listbox.clientHeight) / listbox.scrollHeight;

    // Load more when user has scrolled 80% of the way down
    if (scrollPercentage > 0.9 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({
          field: { value, onChange, ref: fieldRef },
          fieldState: { invalid, error },
        }) => (
          <Autocomplete
            options={options}
            getOptionLabel={(option) =>
              !isEmpty(option) ? `${option?.firstName} ${option?.lastName}` : ""
            }
            getOptionKey={(option) => option.employeeId}
            isOptionEqualToValue={(option, selectedValue) =>
              option.employeeId === selectedValue?.employeeId
            }
            loading={isLoading || isFetchingNextPage}
            value={value || null}
            disableListWrap
            disablePortal
            disabled={disabled}
            onOpen={() => setSearch("")}
            onChange={(__, newValue) => {
              setIsTyping(false);

              setIsRemovedAdornment(!newValue);

              onChange(newValue);
            }}
            onBlur={() => setIsTyping(false)}
            onInputChange={(event, newInputValue, reason) => {
              if (reason !== "reset") {
                setIsTyping(true);
                handleSearchChange(event, newInputValue);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                id={name}
                label={label}
                required={required}
                error={invalid}
                helperText={
                  disabled
                    ? __(
                        "Wniosek zostanie zaakceptowany automatycznie. Akceptant nie jest wymagany."
                      )
                    : error?.message ||
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (error as any)?.presentAtQueriedTimeRange?.message
                }
                inputRef={fieldRef}
                sx={getInputStyles(isTyping, disabled)}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        {!isTyping && !isRemovedAdornment && (
                          <EmployeesAutocompleteAdornment data={value} />
                        )}
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {isLoading && (
                          <CircularProgress color="inherit" size={20} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <EmployeesAutocompleteOption liProps={props} option={option} />
            )}
            slotProps={{
              listbox: {
                onScroll: handleScroll,
                style: {
                  maxHeight: "300px",
                  overflowY: "auto",
                },
              },
            }}
            data-testid={`absence-request-form-field__${name}`}
          />
        )}
      />
    </>
  );
};

export default EmployeesAutocomplete;
